import { useEffect, useState } from 'react';
import './ThemeSwitch.scss';

export default function ThemeSwitch() {
  const [isDark, setIsDark] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Carrega o tema do localStorage na montagem
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches;

    const initialTheme = savedTheme ? savedTheme === 'dark' : prefersDark;

    setIsDark(initialTheme);
    document.documentElement.setAttribute(
      'data-theme',
      initialTheme ? 'dark' : 'light'
    );
    setMounted(true);
  }, []);

  const toggleTheme = () => {
    setIsDark((prev) => {
      const newTheme = !prev;
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      document.documentElement.setAttribute(
        'data-theme',
        newTheme ? 'dark' : 'light'
      );
      return newTheme;
    });
  };

  // Evita flash durante hidratação
  if (!mounted) {
    return (
      <div className="theme-switch-wrapper">
        <button
          className="theme-switch"
          disabled
          aria-label="Carregando tema..."
        >
          <span className="switch-track" />
        </button>
      </div>
    );
  }

  return (
    <div className="theme-switch-wrapper">
      <button
        className={`theme-switch ${isDark ? 'dark' : 'light'}`}
        onClick={toggleTheme}
        aria-label={`Trocar para modo ${isDark ? 'claro' : 'escuro'}`}
        type="button"
      >
        {/* Track (fundo do switch) */}
        <span className="switch-track">
          {/* Background gradiente - muda de azul para escuro */}
          <span className="track-background" />

          {/* Estrelas - aparecem do topo no modo escuro */}
          <svg
            className="stars-layer"
            viewBox="0 0 48 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Estrela grande 1 - topo esquerdo */}
            <g className="star star-1">
              <path
                d="M26.2879 -2.12121L26.3814 -1.75876C26.4675 -1.42504 26.7281 -1.16443 27.0618 -1.07835L27.4242 -0.984848L27.0618 -0.891352C26.7281 -0.805264 26.4675 -0.54466 26.3814 -0.210934L26.2879 0.151515L26.1944 -0.210934C26.1083 -0.54466 25.8477 -0.805264 25.514 -0.891352L25.1515 -0.984848L25.514 -1.07835C25.8477 -1.16443 26.1083 -1.42504 26.1944 -1.75876L26.2879 -2.12121Z"
                fill="white"
              />
            </g>

            {/* Estrela média 2 */}
            <g className="star star-2">
              <path
                d="M21.5151 5.90909L21.565 6.1024C21.6109 6.28039 21.7499 6.41937 21.9279 6.46529L22.1212 6.51515L21.9279 6.56502C21.7499 6.61093 21.6109 6.74992 21.565 6.92791L21.5151 7.12121L21.4653 6.92791C21.4194 6.74992 21.2804 6.61093 21.1024 6.56502L20.9091 6.51515L21.1024 6.46529C21.2804 6.41937 21.4194 6.28039 21.4653 6.1024L21.5151 5.90909Z"
                fill="white"
              />
            </g>

            {/* Estrelas pequenas - círculos - AUMENTADAS */}
            <circle
              className="star star-3"
              cx="25.0758"
              cy="3.71212"
              r="0.5"
              fill="white"
            />
            <circle
              className="star star-4"
              cx="19.0909"
              cy="2.27273"
              r="0.4"
              fill="white"
            />
            <circle
              className="star star-5"
              cx="11.2121"
              cy="1.21212"
              r="0.65"
              fill="white"
            />
            <circle
              className="star star-6"
              cx="11.2121"
              cy="8.33333"
              r="0.4"
              fill="white"
            />
            <circle
              className="star star-7"
              cx="7.87878"
              cy="5.75758"
              r="0.4"
              fill="white"
            />
            <circle
              className="star star-8"
              cx="6.59092"
              cy="7.34848"
              r="0.5"
              fill="white"
            />
          </svg>

          {/* Círculos decorativos de fundo */}
          <svg
            className="decorative-circles"
            viewBox="0 0 48 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="10.9849"
              cy="10.5303"
              r="29.6212"
              fill="white"
              opacity="0.1"
            />
            <circle
              cx="10.9848"
              cy="10.5303"
              r="24.1667"
              fill="white"
              opacity="0.1"
            />
            <circle
              cx="10.9848"
              cy="10.5303"
              r="17.9545"
              fill="white"
              opacity="0.1"
            />
            <circle
              cx="10.9848"
              cy="10.5303"
              r="12.3485"
              fill="white"
              opacity="0.1"
            />
          </svg>

          {/* Nuvens - aparecem/desaparecem do canto inferior direito */}
          <svg
            className="clouds-layer"
            viewBox="0 0 48 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
          >
            {/* Nível 1 de nuvens - mais atrás */}
            <path
              className="cloud-group cloud-back"
              d="M45.6815 0.60606C48.8194 0.60606 51.364 3.1498 51.3641 6.2877C51.3641 8.59934 49.9823 10.5869 48.0008 11.4742C48.0196 11.6621 48.0301 11.8527 48.0301 12.0455C48.0301 15.1835 45.4864 17.7272 42.3484 17.7272C41.4728 17.7271 40.6439 17.5282 39.9031 17.1744C39.4152 19.8165 37.1015 21.818 34.3182 21.818C33.7878 21.818 33.2749 21.7439 32.7879 21.608C31.7554 22.6715 30.3111 23.3336 28.7117 23.3336C27.0167 23.3335 25.4958 22.59 24.4549 21.4127C23.8033 21.6731 23.093 21.818 22.3484 21.818C21.3462 21.818 20.4052 21.5576 19.5877 21.1022C18.5493 22.4578 16.9155 23.3335 15.076 23.3336C13.9864 23.3336 12.969 23.0255 12.1043 22.4938C11.072 23.9198 9.39484 24.8482 7.49982 24.8482C4.36192 24.8481 1.81818 22.3045 1.81818 19.1666C1.81821 16.0287 4.36194 13.4851 7.49982 13.485C8.58879 13.485 9.60613 13.7916 10.4705 14.3229C11.5028 12.8971 13.1812 11.9693 15.076 11.9693C16.0778 11.9694 17.0185 12.23 17.8358 12.6852C18.8741 11.3294 20.5088 10.4547 22.3484 10.4547C24.0434 10.4547 25.5643 11.1975 26.6053 12.3746C27.2568 12.1143 27.9673 11.9694 28.7117 11.9693C29.2416 11.9693 29.7545 12.0436 30.241 12.1793C31.2736 11.1156 32.7186 10.4547 34.3182 10.4547C35.1934 10.4547 36.0221 10.653 36.7625 11.0065C37.1057 9.14939 38.3512 7.60924 40.0281 6.85801C40.0095 6.67051 39.9998 6.48013 39.9998 6.2877C39.9999 3.14992 42.5437 0.606255 45.6815 0.60606Z"
              fill="#ACC9E6"
            />

            {/* Nível 2 de nuvens - mais na frente */}
            <path
              className="cloud-group cloud-front"
              d="M47.1966 4.84848C50.3345 4.84848 52.8791 7.39223 52.8792 10.5301C52.8792 12.8418 51.4975 14.8293 49.516 15.7166C49.5347 15.9046 49.5453 16.0951 49.5453 16.2879C49.5452 19.4259 47.0016 21.9696 43.8636 21.9696C42.988 21.9696 42.1591 21.7706 41.4183 21.4168C40.9303 24.0589 38.6166 26.0604 35.8333 26.0604C35.303 26.0604 34.7901 25.9863 34.3031 25.8504C33.2705 26.9139 31.8262 27.576 30.2269 27.576C28.5318 27.5759 27.011 26.8325 25.9701 25.6551C25.3185 25.9155 24.6081 26.0604 23.8636 26.0604C22.8614 26.0604 21.9204 25.8 21.1029 25.3446C20.0645 26.7003 18.4307 27.5759 16.5912 27.576C15.5013 27.576 14.4833 27.2682 13.6185 26.7362C12.5862 28.162 10.9098 29.0907 9.01498 29.0907C5.87708 29.0906 3.33334 26.547 3.33334 23.409C3.33338 20.2711 5.8771 17.7275 9.01498 17.7274C10.104 17.7274 11.1213 18.0341 11.9857 18.5653C13.018 17.1396 14.6964 16.2118 16.5912 16.2118C17.593 16.2118 18.5337 16.4724 19.3509 16.9276C20.3893 15.5719 22.024 14.6971 23.8636 14.6971C25.5586 14.6971 27.0794 15.4399 28.1205 16.617C28.772 16.3567 29.4825 16.2118 30.2269 16.2118C30.7567 16.2118 31.2696 16.2861 31.7562 16.4217C32.7788 15.358 34.2338 14.6971 35.8333 14.6971C36.7085 14.6971 37.5372 14.8954 38.2777 15.2489C38.6209 13.3918 39.8664 11.8517 41.5433 11.1004C41.5246 10.9129 41.515 10.7226 41.515 10.5301C41.5151 7.39234 44.0589 4.84867 47.1966 4.84848Z"
              fill="white"
            />
          </svg>
        </span>

        {/* Thumb (bolinha que desliza - Sol/Lua) */}
        <span className="switch-thumb">
          {/* Sol - visível no modo claro */}
          <span className="sun" />

          {/* Lua com camadas de glow - visível no modo escuro */}
          <span className="moon">
            {/* Camadas de glow/luz ao redor da lua */}
            <span className="moon-glow moon-glow-1" />
            <span className="moon-glow moon-glow-2" />
            <span className="moon-glow moon-glow-3" />

            {/* Lua principal */}
            <span className="moon-body" />

            {/* Crateras */}
            <span className="moon-crater moon-crater-1" />
            <span className="moon-crater moon-crater-2" />
            <span className="moon-crater moon-crater-3" />
          </span>
        </span>
      </button>
    </div>
  );
}
